import React, {
    createContext, useContext,
    useMemo, useState, useCallback
} from 'react';
import {
    generatePkceValues, generateState, generateAuthURL,
    generateTokenExchange
} from './utils';
import { loginOAuth2 } from './ExternalApi.services'

const AuthStateContext = createContext(null);
const AuthActionsContext = createContext(null);

export function AuthProvider({ children }) {
    const [accessToken, setAccessToken] = useState(null);
    const [expiresAt, setExpiresAt] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const isAuthenticated = !!accessToken && !!expiresAt && Date.now() < expiresAt;

    // --- Generating authorization URL for OAuth2 login ---
    const connect = useCallback(async () => {
        const pkce = await generatePkceValues();
        const state = generateState();

        sessionStorage.setItem("pkce_verifier", pkce.verifier);
        sessionStorage.setItem("oauth_state", state);
        const authUrl = generateAuthURL(
            import.meta.env.VITE_API_AUTH_BASE,
            import.meta.env.VITE_COGNITO_CLIENT_ID,
            import.meta.env.VITE_REDIRECT_URI_DEV,
            state,
            pkce.challenge
        );

        window.location.href = authUrl;
    }, []);

    // --- Handler for callback from OAuth2 login ---
    const handleCallback = useCallback(async (code, returnedState) => {
        setIsLoading(true);
        try {
            const savedState = sessionStorage.getItem("oauth_state");
            const verifier = sessionStorage.getItem("pkce_verifier");

            if (!code) throw new Error("No authorization code in callback");
            if (!savedState || returnedState !== savedState) {
                throw new Error("OAuth state mismatch");
            }
            if (!verifier) throw new Error("PKCE verifier missing");

            const tokenBody = generateTokenExchange(
                import.meta.env.VITE_COGNITO_CLIENT_ID,
                code,
                import.meta.env.VITE_REDIRECT_URI_DEV,
                verifier
            );
            const tokenResp = await loginOAuth2(
                import.meta.env.VITE_API_AUTH_BASE,
                tokenBody
            );

            setAccessToken(tokenResp.access_token);
            setExpiresAt(Date.now() + tokenResp.expires_in * 100);

            return tokenResp.access_token
        } finally {
            sessionStorage.removeItem("oauth_state");
            sessionStorage.removeItem("pkce_verifier");
            setIsLoading(false);
        }
    }, []);

    // --- Token invalidator / logout function ---
    const logout = useCallback(() => {
        setAccessToken(null);
        setExpiresAt(null);
    }, []);

    // --- Authorization states ---
    const stateValue = useMemo(() => ({
        accessToken,
        expiresAt,
        isAuthenticated,
        isLoading
    }), [accessToken, expiresAt, isAuthenticated, isLoading]);

    // --- Authorization actions ---
    const actionsValue = useMemo(() => ({
        connect,
        handleCallback,
        logout
    }), [connect, handleCallback, logout]);

    return (
        <AuthStateContext.Provider value={stateValue}>
            <AuthActionsContext.Provider value={actionsValue}>
                {children}
            </AuthActionsContext.Provider>
        </AuthStateContext.Provider>
    )
}

// --- Hook for authorization states ---
export function useAuthState() {
    const ctx = useContext(AuthStateContext);
    if (!ctx) throw new Error("useAuthState must be used within AuthProvider");
    return ctx;
}

// --- Hook for authorization actions ---
export function useAuthActions() {
    const ctx = useContext(AuthActionsContext);
    if (!ctx) throw new Error("useAuthActions must be used within AuthProvider");
    return ctx;
}